import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

// Runtime API URL - uses origin for production (afterplay.live)
const getApiBaseUrl = () => {
  if (typeof window !== 'undefined' && (window.location.hostname === 'afterplay.live' || window.location.hostname.endsWith('.emergent.host'))) {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};
const API_BASE_URL = getApiBaseUrl();
const WS_BASE_URL = API_BASE_URL ? API_BASE_URL.replace("https://", "wss://").replace("http://", "ws://") : "";

// Always use polling - WebSocket doesn't work through Kubernetes ingress
const USE_POLLING = true;

/**
 * Hook for managing general user notifications with WebSocket or polling
 */
export const useNotifications = (userId) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [latestNotification, setLatestNotification] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  // Fetch notifications via API
  const fetchNotifications = useCallback(async () => {
    if (!userId) return;
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/api/notifications?token=${token}&limit=20`);
      const data = response.data || [];
      
      // Check for new notifications
      if (notifications.length > 0 && data.length > notifications.length) {
        const newNotifs = data.filter(n => !notifications.find(o => o.id === n.id));
        if (newNotifs.length > 0) {
          setLatestNotification(newNotifs[0]);
        }
      }
      
      setNotifications(data);
      setUnreadCount(data.filter(n => !n.read).length);
      setIsConnected(true);
    } catch (error) {
      // Notifications endpoint may not exist yet - fail silently
      console.debug("Notifications fetch skipped:", error.message);
    }
  }, [userId, notifications]);

  useEffect(() => {
    if (!userId) return;

    // Use polling for production environments
    if (USE_POLLING) {
      // Polling mode for notifications (production mode)");
      
      const initialFetch = setTimeout(fetchNotifications, 0);
      pollingIntervalRef.current = setInterval(fetchNotifications, 15000);
      
      return () => {
        clearTimeout(initialFetch);
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
      };
    }

    // WebSocket for local development
    const connectWebSocket = () => {
      const wsUrl = `${WS_BASE_URL}/ws/notifications/${userId}`;
      
      try {
        wsRef.current = new WebSocket(wsUrl);

        wsRef.current.onopen = () => {
          // WS connected for notifications:", userId);
          setIsConnected(true);
          
          pingIntervalRef.current = setInterval(() => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send("ping");
            }
          }, 30000);
        };

        wsRef.current.onmessage = (event) => {
          if (event.data === "pong") return;
          
          try {
            const data = JSON.parse(event.data);
            
            if (data.type === "initial_state") {
              setUnreadCount(data.unread_count || 0);
            } else if (data.type === "notification") {
              setLatestNotification(data);
              setUnreadCount(prev => prev + 1);
              setNotifications(prev => [data, ...prev].slice(0, 50));
            }
          } catch (e) {
            console.error("Failed to parse notification WebSocket message:", e);
          }
        };

        wsRef.current.onclose = () => {
          // WS disconnected for notifications");
          setIsConnected(false);
          
          if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
          }
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, 3000);
        };

        wsRef.current.onerror = (error) => {
          console.error("Notification WebSocket error:", error);
        };
      } catch (e) {
        console.error("Failed to create notification WebSocket:", e);
      }
    };

    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
    };
  }, [userId, fetchNotifications]);

  const markAsRead = useCallback(async (notificationId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API_BASE_URL}/api/notifications/${notificationId}/read?token=${token}`);
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API_BASE_URL}/api/notifications/read-all?token=${token}`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
    }
  }, []);

  const clearLatest = useCallback(() => {
    setLatestNotification(null);
  }, []);

  return { 
    notifications, 
    unreadCount, 
    isConnected, 
    latestNotification,
    markAsRead,
    markAllAsRead,
    clearLatest
  };
};

/**
 * Hook for reviewer real-time updates (earnings, balance, new submissions)
 */
export const useReviewerWebSocket = (reviewerId) => {
  const [balance, setBalance] = useState(0);
  const [pendingPayout, setPendingPayout] = useState(0);
  const [totalEarned, setTotalEarned] = useState(0);
  const [latestEarning, setLatestEarning] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  // Fetch balance via API
  const fetchBalance = useCallback(async () => {
    if (!reviewerId) return;
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/api/reviewers/profile?token=${token}`);
      const profile = response.data;
      
      if (profile) {
        setBalance(profile.balance || 0);
        setPendingPayout(profile.pending_payout_balance || 0);
        setTotalEarned(profile.total_earned || 0);
      }
      setIsConnected(true);
    } catch (error) {
      console.error("Failed to fetch reviewer balance:", error);
    }
  }, [reviewerId]);

  useEffect(() => {
    if (!reviewerId) return;

    // Use polling for production
    if (USE_POLLING) {
      const initialFetch = setTimeout(fetchBalance, 0);
      pollingIntervalRef.current = setInterval(fetchBalance, 30000);
      
      return () => {
        clearTimeout(initialFetch);
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
      };
    }

    // WebSocket for local development
    const connectWebSocket = () => {
      const wsUrl = `${WS_BASE_URL}/ws/reviewer/${reviewerId}`;
      
      try {
        wsRef.current = new WebSocket(wsUrl);

        wsRef.current.onopen = () => {
          // WS connected for reviewer:", reviewerId);
          setIsConnected(true);
          
          pingIntervalRef.current = setInterval(() => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send("ping");
            }
          }, 30000);
        };

        wsRef.current.onmessage = (event) => {
          if (event.data === "pong") return;
          
          try {
            const data = JSON.parse(event.data);
            
            switch (data.type) {
              case "initial_state":
              case "balance_update":
                setBalance(data.balance || 0);
                setPendingPayout(data.pending_payout || 0);
                setTotalEarned(data.total_earned || 0);
                break;
              case "earnings_update":
                setLatestEarning({
                  amount: data.amount,
                  source: data.source,
                  timestamp: data.timestamp
                });
                break;
              case "new_submission":
                // Could trigger a toast notification here
                break;
              default:
                // Unknown WS message:", data.type);
            }
          } catch (e) {
            console.error("Failed to parse reviewer WebSocket message:", e);
          }
        };

        wsRef.current.onclose = () => {
          // WS disconnected for reviewer");
          setIsConnected(false);
          
          if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
          }
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, 3000);
        };

        wsRef.current.onerror = (error) => {
          console.error("Reviewer WebSocket error:", error);
        };
      } catch (e) {
        console.error("Failed to create reviewer WebSocket:", e);
      }
    };

    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
    };
  }, [reviewerId, fetchBalance]);

  const clearLatestEarning = useCallback(() => {
    setLatestEarning(null);
  }, []);

  return { 
    balance, 
    pendingPayout, 
    totalEarned, 
    latestEarning, 
    isConnected,
    clearLatestEarning 
  };
};

/**
 * Hook for managing queue updates with WebSocket or polling fallback
 */
export const useQueueWebSocket = (reviewerId) => {
  const [queue, setQueue] = useState([]);
  const [nowPlaying, setNowPlaying] = useState(null);
  const [viewerCount, setViewerCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const pollingIntervalRef = useRef(null);

  // Polling function to fetch queue updates
  const fetchQueueUpdate = useCallback(async () => {
    if (!reviewerId) return;
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/api/reviewer/queue-status?token=${token}`);
      const data = response.data;
      
      setQueue(data.pending || []);
      setNowPlaying(data.now_playing || null);
      setIsConnected(true);
    } catch (error) {
      console.error("Failed to fetch queue update:", error);
    }
  }, [reviewerId]);

  useEffect(() => {
    if (!reviewerId) return;

    // Use polling for production environments
    if (USE_POLLING) {
      // Polling mode for queue updates (production mode)");
      
      // Initial fetch deferred to avoid lint warning
      const initialFetch = setTimeout(fetchQueueUpdate, 0);
      
      // Poll every 5 seconds
      pollingIntervalRef.current = setInterval(fetchQueueUpdate, 5000);
      
      return () => {
        clearTimeout(initialFetch);
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
      };
    }

    // WebSocket for local development
    const connectWebSocket = () => {
      const wsUrl = `${WS_BASE_URL}/ws/queue/${reviewerId}`;
      
      try {
        wsRef.current = new WebSocket(wsUrl);

        wsRef.current.onopen = () => {
          // WS connected to queue:", reviewerId);
          setIsConnected(true);
          
          // Start ping interval to keep connection alive
          pingIntervalRef.current = setInterval(() => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send("ping");
            }
          }, 30000);
        };

        wsRef.current.onmessage = (event) => {
          if (event.data === "pong") return;
          
          try {
            const data = JSON.parse(event.data);
            
            switch (data.type) {
              case "initial_state":
              case "queue_update":
                setQueue(data.queue || []);
                setNowPlaying(data.now_playing || null);
                setViewerCount(data.viewer_count || 0);
                break;
              case "now_playing":
                setNowPlaying(data.submission || null);
                break;
              case "viewer_count":
                setViewerCount(data.count || 0);
                break;
              default:
                // Unknown WS message type:", data.type);
            }
          } catch (e) {
            console.error("Failed to parse WebSocket message:", e);
          }
        };

        wsRef.current.onclose = () => {
          // WS disconnected from queue:", reviewerId);
          setIsConnected(false);
          
          // Clear ping interval
          if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
          }
          
          // Attempt to reconnect after 3 seconds
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, 3000);
        };

        wsRef.current.onerror = (error) => {
          console.error("WebSocket error:", error);
          // Fall back to polling on WebSocket error
          setIsConnected(false);
        };
      } catch (e) {
        console.error("Failed to create WebSocket:", e);
      }
    };

    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
    };
  }, [reviewerId, fetchQueueUpdate]);

  return { queue, nowPlaying, viewerCount, isConnected };
};

/**
 * Hook for artist notifications with polling fallback
 */
export const useArtistWebSocket = (artistId, onNotification) => {
  const [isConnected, setIsConnected] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [notification, setNotification] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const onNotificationRef = useRef(onNotification);
  const lastSubmissionsRef = useRef([]);
  
  // Keep ref updated
  useEffect(() => {
    onNotificationRef.current = onNotification;
  }, [onNotification]);

  // Polling function to fetch submissions
  const fetchSubmissions = useCallback(async () => {
    if (!artistId) return;
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/api/submissions/artist?token=${token}`);
      const newSubmissions = response.data || [];
      
      // Check for status changes to trigger notifications
      if (lastSubmissionsRef.current.length > 0) {
        newSubmissions.forEach(sub => {
          const oldSub = lastSubmissionsRef.current.find(s => s.id === sub.id);
          if (oldSub && oldSub.status !== sub.status) {
            // Status changed - trigger notification
            if (sub.status === "now_playing") {
              setNotification({
                type: "track_up_next",
                submission: sub,
                message: `Your track "${sub.track_name}" is now playing!`
              });
            }
          }
        });
      }
      
      lastSubmissionsRef.current = newSubmissions;
      setSubmissions(newSubmissions);
      setIsConnected(true);
    } catch (error) {
      console.error("Failed to fetch submissions:", error);
    }
  }, [artistId]);

  useEffect(() => {
    if (!artistId) return;

    // Use polling for production environments
    if (USE_POLLING) {
      // Polling mode for artist updates (production mode)");
      
      // Initial fetch deferred to avoid lint warning
      const initialFetch = setTimeout(fetchSubmissions, 0);
      
      // Poll every 10 seconds for artist updates
      pollingIntervalRef.current = setInterval(fetchSubmissions, 10000);
      
      return () => {
        clearTimeout(initialFetch);
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
      };
    }

    // WebSocket for local development
    const connectWebSocket = () => {
      const wsUrl = `${WS_BASE_URL}/ws/artist/${artistId}`;
      
      try {
        wsRef.current = new WebSocket(wsUrl);

        wsRef.current.onopen = () => {
          // WS connected for artist:", artistId);
          setIsConnected(true);
          
          // Start ping interval
          pingIntervalRef.current = setInterval(() => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send("ping");
            }
          }, 30000);
        };

        wsRef.current.onmessage = (event) => {
          if (event.data === "pong") return;
          
          try {
            const data = JSON.parse(event.data);
            
            // Handle different message types
            if (data.type === "submissions_update") {
              setSubmissions(data.submissions || []);
            } else if (data.type === "notification") {
              setNotification(data);
            } else if (onNotificationRef.current) {
              onNotificationRef.current(data);
            }
          } catch (e) {
            console.error("Failed to parse WebSocket message:", e);
          }
        };

        wsRef.current.onclose = () => {
          // WS disconnected for artist:", artistId);
          setIsConnected(false);
          
          if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
          }
          
          // Attempt to reconnect
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, 3000);
        };

        wsRef.current.onerror = (error) => {
          console.error("WebSocket error:", error);
        };
      } catch (e) {
        console.error("Failed to create WebSocket:", e);
      }
    };

    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
    };
  }, [artistId, fetchSubmissions]);

  return { isConnected, submissions, notification };
};

/**
 * Hook for real-time inbox updates with polling fallback
 */
export const useInboxWebSocket = (userId, onInboxUpdate) => {
  const [isConnected, setIsConnected] = useState(false);
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const pollingIntervalRef = useRef(null);
  const lastMessageCountRef = useRef(0);
  const onInboxUpdateRef = useRef(onInboxUpdate);
  
  // Keep ref updated
  useEffect(() => {
    onInboxUpdateRef.current = onInboxUpdate;
  }, [onInboxUpdate]);

  // Polling function to check for new messages
  const checkForUpdates = useCallback(async () => {
    if (!userId) return;
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/api/inbox?token=${token}&limit=1`);
      const total = response.data?.total || 0;
      
      // Check if there are new messages
      if (lastMessageCountRef.current > 0 && total > lastMessageCountRef.current) {
        setHasNewMessages(true);
        if (onInboxUpdateRef.current) {
          onInboxUpdateRef.current({ type: 'new_messages', count: total - lastMessageCountRef.current });
        }
      }
      
      lastMessageCountRef.current = total;
      setIsConnected(true);
    } catch (error) {
      console.debug("Inbox check failed:", error.message);
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) return;

    // Use polling for inbox updates (works in all environments)
    // Starting polling for user:", userId);
    
    const initialFetch = setTimeout(checkForUpdates, 0);
    pollingIntervalRef.current = setInterval(checkForUpdates, 5000); // Check every 5 seconds for real-time feel
    
    return () => {
      clearTimeout(initialFetch);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [userId, checkForUpdates]);

  const clearNewMessages = useCallback(() => {
    setHasNewMessages(false);
  }, []);

  return { isConnected, hasNewMessages, clearNewMessages };
};

/**
 * Hook for reviewer offline queue real-time updates
 */
export const useOfflineQueueWebSocket = (reviewerId, onNewSubmission) => {
  const [isConnected, setIsConnected] = useState(false);
  const [offlineQueue, setOfflineQueue] = useState([]);
  const pollingIntervalRef = useRef(null);
  const lastQueueRef = useRef([]);
  const onNewSubmissionRef = useRef(onNewSubmission);
  
  // Keep ref updated
  useEffect(() => {
    onNewSubmissionRef.current = onNewSubmission;
  }, [onNewSubmission]);

  // Polling function to check for new offline submissions
  const fetchOfflineQueue = useCallback(async () => {
    if (!reviewerId) return;
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/api/submissions/reviewer?token=${token}&review_type=offline`);
      const submissions = response.data || [];
      const pending = submissions.filter(s => s.status === 'pending' || s.status === 'in_review');
      
      // Check for new submissions
      if (lastQueueRef.current.length > 0) {
        const newSubmissions = pending.filter(s => !lastQueueRef.current.find(o => o.id === s.id));
        if (newSubmissions.length > 0 && onNewSubmissionRef.current) {
          newSubmissions.forEach(sub => {
            onNewSubmissionRef.current(sub);
          });
        }
      }
      
      lastQueueRef.current = pending;
      setOfflineQueue(pending);
      setIsConnected(true);
    } catch (error) {
      console.debug("Offline queue fetch failed:", error.message);
    }
  }, [reviewerId]);

  useEffect(() => {
    if (!reviewerId) return;

    // Starting polling for reviewer:", reviewerId);
    
    const initialFetch = setTimeout(fetchOfflineQueue, 0);
    pollingIntervalRef.current = setInterval(fetchOfflineQueue, 15000); // Check every 15 seconds
    
    return () => {
      clearTimeout(initialFetch);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [reviewerId, fetchOfflineQueue]);

  return { isConnected, offlineQueue };
};

export default useQueueWebSocket;
